import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';

import defaultCampusBg from '@lok/assets/backgrounds/campus-default.jpg';

const HEX_RE = /^#?([0-9a-fA-F]{6})$/;

const hexToRgb = (hex) => {
  if (!hex) return null;
  const match = HEX_RE.exec(String(hex).trim());
  if (!match) return null;
  const v = match[1];
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16)
  };
};

export const hexToRgbTriplet = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
};

// WCAG relative luminance; threshold ~0.5 picks dark text on light surfaces
// and light text on dark surfaces. Returns the foreground RGB triplet to
// pair with the given accent.
const contrastFgTriplet = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L =
    0.2126 * channel(rgb.r) +
    0.7152 * channel(rgb.g) +
    0.0722 * channel(rgb.b);
  // Dark navy for light accents, white for dark accents.
  return L > 0.5 ? '15 22 32' : '255 255 255';
};

const setVar = (root, name, value) => {
  if (value) root.style.setProperty(name, value);
  else root.style.removeProperty(name);
};

const BRAND_BG_SCRIM =
  'linear-gradient(rgb(0 0 0 / 0.24), rgb(0 0 0 / 0.24))';

const withBackgroundScrim = (imageUrl) =>
  imageUrl ? `${BRAND_BG_SCRIM}, url("${imageUrl}")` : null;

// Branding is single-tenant: the admin's own university, applied globally
// across the platform. It does NOT switch per course or per page.
export const resolveBrandingUniversity = (user) =>
  user?.university || user?.admins?.[0]?.university || null;

// Primary (surface) color is locked to the mode default (white/black) and is
// NOT user-editable. Only the accent (stored as secondary_color for backend
// compatibility) and background images flow through here.
export const applyBrandingToRoot = ({
  secondary_color,
  background_image,
  background_image_dark
} = {}) => {
  const root = document.documentElement;
  setVar(root, '--brand-secondary', hexToRgbTriplet(secondary_color));
  setVar(root, '--brand-secondary-fg', contrastFgTriplet(secondary_color));
  setVar(
    root,
    '--brand-bg-image',
    withBackgroundScrim(background_image)
  );
  setVar(
    root,
    '--brand-bg-image-dark',
    withBackgroundScrim(background_image_dark)
  );
};

// Branding context. `activeUniversity` is the university whose branding is
// currently painted. It's normally the logged-in user's own university, but a
// super-admin drilling into a client sets a `viewedUniversity` override so the
// whole platform chrome (accent + bg image + "Powered by Continuum" gate)
// reflects that client while they're viewing it. Call `setViewedUniversity`
// with the client on mount and `null` on unmount.
const BrandingCtx = createContext({
  activeUniversity: null,
  hasBackgroundImage: false,
  setViewedUniversity: () => {}
});

export const useBranding = () => useContext(BrandingCtx);

export const BrandingProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const [viewedUniversity, setViewedUniversity] = useState(null);

  const activeUniversity =
    viewedUniversity || resolveBrandingUniversity(user);

  const secondary = activeUniversity?.secondary_color;

  // Non-whitelabeled accounts (including accounts with no university at all)
  // default to the bundled campus background instead of a blank page (Dane,
  // CON-197). Whitelabeled clients keep full control: their own images or
  // nothing, never the Continuum default.
  const isWhitelabel = Boolean(activeUniversity?.is_whitelabel);
  const defaultBg = isWhitelabel ? null : defaultCampusBg;
  const bgLight = activeUniversity?.background_image || defaultBg;
  const bgDark = activeUniversity?.background_image_dark || defaultBg;

  useEffect(() => {
    applyBrandingToRoot({
      secondary_color: secondary,
      background_image: bgLight,
      background_image_dark: bgDark
    });
  }, [secondary, bgLight, bgDark]);

  // hasBackgroundImage reflects the EFFECTIVE background (university image
  // or the campus default), not just the university fields. MainLayout uses
  // it to lighten its panels; keying that off the raw fields left the
  // campus-default fallback painted but hidden behind ~99% opaque panels.
  const hasBackgroundImage = Boolean(bgLight || bgDark);

  const value = useMemo(
    () => ({
      activeUniversity,
      hasBackgroundImage,
      setViewedUniversity
    }),
    [activeUniversity, hasBackgroundImage]
  );

  return (
    <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>
  );
};
